import { Lock, ShieldCheck, ServerCog, DatabaseBackup, KeyRound, FileCheck2 } from "lucide-react";

type Bi = { en: string; bn: string };
const pick = (b: Bi, isBn: boolean) => (isBn ? b.bn : b.en);

interface Props {
  isBn: boolean;
  displayFont?: string;
}

// Brand-tinted "logo tile" — first letter on the brand color. Matches the
// existing integrations grid aesthetic so it feels native, and avoids shipping
// third-party logo assets that would need trademark clearance.
const LOGOS: { name: string; tag: Bi; color: string }[] = [
  { name: "WhatsApp", tag: { en: "Messaging", bn: "মেসেজিং" }, color: "#25d366" },
  { name: "bKash", tag: { en: "Payments", bn: "পেমেন্ট" }, color: "#e2136e" },
  { name: "Nagad", tag: { en: "Payments", bn: "পেমেন্ট" }, color: "#ec1c24" },
  { name: "SSLCommerz", tag: { en: "Cards", bn: "কার্ড" }, color: "#1d3f77" },
  { name: "Twilio", tag: { en: "SMS", bn: "এসএমএস" }, color: "#f22f46" },
  { name: "Google Calendar", tag: { en: "Scheduling", bn: "সময়সূচি" }, color: "#4285f4" },
  { name: "Meta Cloud API", tag: { en: "WhatsApp API", bn: "WhatsApp API" }, color: "#1877f2" },
  { name: "Zoom", tag: { en: "Tele-visit", bn: "টেলি-ভিজিট" }, color: "#2d8cff" },
  { name: "FHIR", tag: { en: "EMR export", bn: "EMR এক্সপোর্ট" }, color: "#7c3aed" },
  { name: "OpenEMR", tag: { en: "Records", bn: "রেকর্ড" }, color: "#25c39a" },
];

const BADGES: { icon: typeof ShieldCheck; label: Bi; sub: Bi }[] = [
  {
    icon: Lock,
    label: { en: "TLS 1.3 in transit", bn: "TLS 1.3 এনক্রিপশন" },
    sub: { en: "All traffic encrypted", bn: "সব ট্রাফিক এনক্রিপ্টেড" },
  },
  {
    icon: KeyRound,
    label: { en: "AES-256 at rest", bn: "AES-256 এনক্রিপশন" },
    sub: { en: "Encrypted storage", bn: "এনক্রিপ্টেড স্টোরেজ" },
  },
  {
    icon: ShieldCheck,
    label: { en: "Row-level access", bn: "রো-লেভেল অ্যাক্সেস" },
    sub: { en: "Per-chamber isolation", bn: "চেম্বার-ভিত্তিক আলাদা" },
  },
  {
    icon: DatabaseBackup,
    label: { en: "Daily backups", bn: "প্রতিদিন ব্যাকআপ" },
    sub: { en: "Point-in-time restore", bn: "পয়েন্ট-ইন-টাইম রিস্টোর" },
  },
  {
    icon: ServerCog,
    label: { en: "In-region hosting", bn: "স্থানীয় হোস্টিং" },
    sub: { en: "Singapore / Mumbai edge", bn: "সিঙ্গাপুর / মুম্বাই এজ" },
  },
  {
    icon: FileCheck2,
    label: { en: "BMDC-aligned Rx", bn: "BMDC-সংগত Rx" },
    sub: { en: "Reg. no. on every page", bn: "প্রতি পাতায় Reg. no." },
  },
];

export default function TrustAndIntegrations({ isBn, displayFont }: Props) {
  const dFont = displayFont ?? "'Fraunces', ui-serif, Georgia, serif";
  // Duplicate for a seamless marquee loop
  const marquee = [...LOGOS, ...LOGOS];

  return (
    <section className="mt-16 md:mt-24" aria-label={isBn ? "ইন্টিগ্রেশন ও নিরাপত্তা" : "Integrations and security"}>
      {/* Integrations marquee */}
      <div className="text-center mb-6">
        <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#64748b]">
          {isBn ? "সংযুক্ত ইন্টিগ্রেশন" : "Plug-and-play integrations"}
        </p>
      </div>

      <div
        className="relative overflow-hidden rounded-2xl border border-[#e2e8f0] bg-white/70 backdrop-blur-sm py-4"
        style={{
          WebkitMaskImage:
            "linear-gradient(to right, transparent, black 8%, black 92%, transparent)",
          maskImage:
            "linear-gradient(to right, transparent, black 8%, black 92%, transparent)",
        }}
      >
        <div className="flex gap-3 animate-[marquee_38s_linear_infinite] hover:[animation-play-state:paused] whitespace-nowrap">
          {marquee.map((it, i) => (
            <div
              key={`${it.name}-${i}`}
              className="shrink-0 inline-flex items-center gap-2.5 pl-2 pr-4 py-1.5 rounded-full border border-[#e2e8f0] bg-white hover:border-[#cbd5e1] hover:shadow-sm transition"
            >
              <span
                className="inline-flex items-center justify-center w-7 h-7 rounded-full text-white text-[12px] font-black shrink-0"
                style={{ backgroundColor: it.color }}
                aria-hidden
              >
                {it.name[0]}
              </span>
              <span className="text-[13px] font-semibold text-[#0f172a] tracking-tight">
                {it.name}
              </span>
              <span className="text-[10.5px] uppercase tracking-wider text-[#94a3b8]">
                {pick(it.tag, isBn)}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Compliance / security badges */}
      <div className="mt-10 text-center mb-6">
        <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#64748b]">
          {isBn ? "সুরক্ষা ও দায়িত্বশীলতা" : "Security & responsibility"}
        </p>
        <h3
          className="mt-2 text-2xl md:text-3xl text-[#0f172a] tracking-tight"
          style={{ fontFamily: dFont }}
        >
          {isBn ? "রোগীর ডেটার জন্য গুরুত্বপূর্ণ সুরক্ষা" : "Controls that matter for patient data"}
        </h3>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {BADGES.map((b) => {
          const Icon = b.icon;
          return (
            <div
              key={b.label.en}
              className="group flex flex-col items-start gap-2 rounded-2xl border border-[#e2e8f0] bg-white p-4 hover:border-[#9ce6cd] hover:shadow-[0_10px_30px_-15px_rgba(37,195,154,0.35)] transition-all"
            >
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-[#e6faf3] text-[#1ba179] group-hover:bg-[#1ba179] group-hover:text-white transition-colors">
                <Icon className="w-4 h-4" strokeWidth={2.2} />
              </span>
              <p className="text-[13px] font-semibold text-[#0f172a] leading-tight">
                {pick(b.label, isBn)}
              </p>
              <p className="text-[11px] text-[#64748b] leading-snug">
                {pick(b.sub, isBn)}
              </p>
            </div>
          );
        })}
      </div>

      <p className="mt-5 text-center text-[11px] text-[#94a3b8] max-w-2xl mx-auto leading-relaxed">
        {isBn
          ? "উল্লিখিত নিয়ন্ত্রণগুলো এই অ্যাপে চালু আছে। এটি কোনো তৃতীয় পক্ষের সার্টিফিকেশন নয়; চেম্বার-নির্দিষ্ট সম্মতির জন্য অতিরিক্ত ব্যবস্থা আপনার দায়িত্ব।"
          : "Controls listed above are enabled on this app. This is not a third-party certification — chamber-specific compliance remains the practice owner's responsibility."}
      </p>

      <style>{`@keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>
    </section>
  );
}
